import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { menuItemRevenueAPI } from '../../services';
import { toast } from 'react-toastify';
import { useBranch } from '../../hooks';
import MonthPickerComponent from '../../components/fields/MonthPickerComponent';

const formatCurrency = (value) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
    Number(value || 0),
  );

const formatPeriod = (period) => {
  if (!period) return '-';
  const [year, month] = period.split('-');
  return `${month}/${year}`;
};

/** Modal chi tiết: từng món của một nhân viên */
const DetailModal = ({ show, onHide, summary }) => {
  if (!summary) return null;
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Chi tiết: {summary.employee_name} — {formatPeriod(summary.period)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Table striped bordered responsive size="sm">
          <thead className="table-light">
            <tr>
              <th>Tên món</th>
              <th className="text-end">Số orders</th>
              <th className="text-end">Doanh thu chia sẻ</th>
            </tr>
          </thead>
          <tbody>
            {summary.items.map((item) => (
              <tr key={item.id}>
                <td>{item.menu_item_name}</td>
                <td className="text-end">{item.total_orders}</td>
                <td className="text-end">{formatCurrency(item.revenue_share)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="table-primary fw-bold">
              <td>Tổng cộng</td>
              <td className="text-end">{summary.total_orders}</td>
              <td className="text-end">{formatCurrency(summary.total_revenue_share)}</td>
            </tr>
          </tfoot>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Đóng
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default function MenuItemRevenueManagementPage() {
  const { selectedBranch } = useBranch();

  // Current period: default current month
  const now = new Date();
  const defaultPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const [period, setPeriod] = useState(defaultPeriod);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const fetchData = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    try {
      const res = await menuItemRevenueAPI.getList(period, selectedBranch?.id);
      setData(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      toast.error('Không thể tải dữ liệu: ' + (err?.message || ''));
    } finally {
      setLoading(false);
    }
  }, [period, selectedBranch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCalculate = async () => {
    if (!period) return;
    setCalculating(true);
    try {
      const res = await menuItemRevenueAPI.calculate(period, selectedBranch?.id);
      toast.success(
        `Tính toán hoàn tất: ${res.calculated || 0} bản ghi — Tổng ${formatCurrency(res.total_revenue_share || 0)}`,
      );
      await fetchData();
    } catch (err) {
      toast.error('Lỗi khi tính toán: ' + (err?.message || ''));
    } finally {
      setCalculating(false);
    }
  };

  const totalShare = data.reduce((s, d) => s + (d.total_revenue_share || 0), 0);
  const totalOrders = data.reduce((s, d) => s + (d.total_orders || 0), 0);

  return (
    <PageLayout>
      <Row className="g-3">
        {/* Header */}
        <Col xs={12}>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <h4 className="mb-1">Chia sẻ doanh thu — Nhân viên sáng tạo món</h4>
              <small className="text-muted">
                Tính theo tần suất khách chọn món. Cộng vào thưởng cuối tháng trong bảng lương.
              </small>
            </div>
            <Button
              variant="primary"
              onClick={handleCalculate}
              disabled={calculating || !period}
            >
              {calculating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Đang tính...
                </>
              ) : (
                '🔄 Tính toán kỳ này'
              )}
            </Button>
          </div>
        </Col>

        {/* Filters */}
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Body className="py-2">
              <Row className="g-2 align-items-end">
                <Col xs={12} md={4}>
                  <Form.Label className="mb-1 small">Kỳ (tháng)</Form.Label>
                  <MonthPickerComponent
                    value={period}
                    onChange={(val) => setPeriod(val)}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Summary cards */}
        <Col xs={6} md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <div className="text-muted small">Số nhân viên</div>
              <h4 className="mb-0">{data.length}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <div className="text-muted small">Tổng orders</div>
              <h4 className="mb-0">{totalOrders.toLocaleString('vi-VN')}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="shadow-sm text-center border-primary">
            <Card.Body>
              <div className="text-muted small">Tổng doanh thu chia sẻ</div>
              <h4 className="mb-0 text-primary">{formatCurrency(totalShare)}</h4>
            </Card.Body>
          </Card>
        </Col>

        {/* Table */}
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h6 className="mb-0">Kỳ: {formatPeriod(period)}</h6>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <Table striped bordered hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Nhân viên</th>
                      <th className="text-center">Số món</th>
                      <th className="text-center">Tổng orders</th>
                      <th className="text-end">Doanh thu chia sẻ</th>
                      <th className="text-center">Trạng thái</th>
                      <th className="text-center">Chi tiết</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          Không có dữ liệu. Hãy bấm "Tính toán kỳ này" để tính.
                        </td>
                      </tr>
                    )}
                    {data.map((row, idx) => (
                      <tr key={row.employee_id}>
                        <td>{idx + 1}</td>
                        <td className="fw-semibold">{row.employee_name}</td>
                        <td className="text-center">{row.total_menu_items}</td>
                        <td className="text-center">{row.total_orders}</td>
                        <td className="text-end fw-bold">{formatCurrency(row.total_revenue_share)}</td>
                        <td className="text-center">
                          {row.is_included_in_payroll ? (
                            <Badge bg="success">Đã vào bảng lương</Badge>
                          ) : (
                            <Badge bg="secondary">Chờ</Badge>
                          )}
                        </td>
                        <td className="text-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setDetailItem(row)}
                          >
                            Xem
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {data.length > 0 && (
                    <tfoot>
                      <tr className="table-primary fw-bold">
                        <td colSpan={3}>Tổng cộng</td>
                        <td className="text-center">{totalOrders.toLocaleString('vi-VN')}</td>
                        <td className="text-end">{formatCurrency(totalShare)}</td>
                        <td colSpan={2} />
                      </tr>
                    </tfoot>
                  )}
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <DetailModal
        show={!!detailItem}
        onHide={() => setDetailItem(null)}
        summary={detailItem}
      />
    </PageLayout>
  );
}
